import { getLocaleAsset, getThumbnail } from "@tosize/ui";
import type { ApiData } from "../components/renderer/data";

export function getImage(
	lang: string,
	material: ApiData["materials"][number],
	watermarked = false,
) {
	const pimImage = material.product?.pimImageDetail
		? (JSON.parse(material.product?.pimImageDetail) as { id: string; path: string })
		: undefined;

	if (pimImage) {
		const image = {
			filename: pimImage.path.split("/").pop() ?? "",
			fullpath: pimImage.path,
			id: pimImage.id,
		};

		if (watermarked) {
			return getThumbnail(image, lang, "watermark_310x155");
		}

		return getThumbnail(image, lang, "default_620x310");
	}
}

export const materialsSku = {
	black: ["100391-6851-18", "100391-6851-28", "100391-6851-38"], //Zwart Eiken meubelpaneel
	oak: ["100525-6838-18", "100525-6838-28", "100525-6838-38"], //Naturel eiken meubelpaneel
	mdf: ["mdf-blank-18mm", "mdf-blank-30mm", "mdf-blank-38mm"], //MDF Blank
	mdf_prime: ["10033-4082-18", "10033-4082-30", "10033-4082-38"], //MDF Gegrond
	fb_025_cst: ["100382-6832-18", "100382-6832-28", "100382-6832-38"], //Wit meubelpaneel
	fb_u115_cst: ["100616-6973-18", "100616-6973-28"], //U115 CST Dark ecru meubelpaneel
	fb_h597_w07: ["100506-6938-18", "100506-6938-28"], //H597 W07 Oslo oak cocoa brown meubelpaneel
	fb_113_cst: ["100385-6844-18", "100385-6844-28"], //113 CST Elegant black meubelpaneel
	fb_u146_cst: ["100668-6995-18", "100668-6995-28"], //U146 CST Cloud grey meubelpaneel
	fb_ud81_cst: ["100795-6841-18", "100795-6841-28"], //UD81 CST Quartz meubelpaneel
	fb_u129_cst: ["100652-6979-18", "100652-6979-28"], //U129 CST Shadow meubelpaneel
	fb_h397_bst: ["100477-6840-18", "100477-6840-28"], //H397 BST Robson oak meubelpaneel
	fb_u147_cst: ["100670-6997-18", "100670-6997-28"], //U147 CST Seashell meubelpaneel
	fb_h906_w06: ["100538-6968-18", "100538-6968-28"], //H906 W06 Missoury hickory light meubelpaneel
	fb_625_cst: ["100396-6843-18", "100396-6843-28"], //625 CST Silicon meubelpaneel
	fb_u653_bst: ["100770-7038-18", "100770-7038-28"], //U653 BST Green shadow meubelpaneel
	fb_h251_w06: ["100460-6893-18", "100460-6893-28"], //H251 W06 Lorenzo walnut medium brown meubelpaneel
	fb_f256_m02: ["100411-6866-18", "100411-6866-28"], //F256 M02 Lime earth baked meubelpaneel
	fb_h265_v1a: ["100463-6896-18", "100463-6896-28"], //H265 V1A Dainty oak cafe Noir meubelpaneel
	fb_f261_m02: ["10068-10963-18", "10068-10963-28"], //F261 M02 Limemoon grey meubelpaneel
	fb_f257_m02: ["100412-6867-18", "100412-6867-28"], //F257 M02 Lime midnight meubelpaneel
	// // fb_u653_cst: ['10048-10953-28'], //U653 CST Green shadow meubelpaneel - Disabled for now
	fb_u311_bst: ["100748-7018-18", "100748-7018-28"], //U311 BST Roasted brown meubelpaneel
	fb_h267_v1a: ["100465-6898-18", "100465-6898-28"], //H267 V1A Dainty oak latte meubelpaneel
} as const;

export const textureAssets = {
	black: {
		map: getLocaleAsset("tosize_furniture/texture/zwart/test/black-texture.jpg"),
		specular: getLocaleAsset("tosize_furniture/texture/zwart/test/gloss-0.5k.jpg"),
	},
	oak: {
		map: getLocaleAsset("tosize_furniture/texture/oak/atlas/texture.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/bst.jpg"),
	},
	mdf: {
		map: getLocaleAsset("tosize_furniture/texture/mdf-natural/test/texture.jpg"),
	},
	mdf_prime: {
		map: getLocaleAsset("tosize_furniture/texture/mdf-prime/mdf-prime-texture.jpg"),
	},
	fb_025_cst: {
		map: getLocaleAsset("tosize_furniture/texture/wit/test/white-texture.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/cst.jpg"),
	},
	fb_u147_cst: {
		map: getLocaleAsset("tosize_furniture/texture/u147_cst/u147_cst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/cst.jpg"),
	},
	fb_625_cst: {
		map: getLocaleAsset("tosize_furniture/texture/625_cst/625_cst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/cst.jpg"),
	},
	fb_u146_cst: {
		map: getLocaleAsset("tosize_furniture/texture/u146_cst/u146_cst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/cst.jpg"),
	},
	fb_u115_cst: {
		map: getLocaleAsset("tosize_furniture/texture/u115_cst/u115_cst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/cst.jpg"),
	},
	fb_u129_cst: {
		map: getLocaleAsset("tosize_furniture/texture/u129_cst/u129_cst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/cst.jpg"),
	},
	fb_ud81_cst: {
		map: getLocaleAsset("tosize_furniture/texture/ud81_cst/ud81_cst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/cst.jpg"),
	},
	fb_113_cst: {
		map: getLocaleAsset("tosize_furniture/texture/113_cst/113_cst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/cst.jpg"),
	},
	fb_u311_bst: {
		map: getLocaleAsset("tosize_furniture/texture/u311_bst/u311_bst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/bst.jpg"),
	},
	fb_u653_bst: {
		map: getLocaleAsset("tosize_furniture/texture/u653_bst/u653_bst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/bst.jpg"),
	},
	fb_f256_m02: {
		map: getLocaleAsset("tosize_furniture/texture/f256_m02/f256_m02.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/m02-2.jpg"),
	},
	fb_f257_m02: {
		map: getLocaleAsset("tosize_furniture/texture/f257_m02/f257_m02.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/m02-2.jpg"),
	},
	fb_f261_m02: {
		map: getLocaleAsset("tosize_furniture/texture/f261_m02/f261_m02.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/m02-2.jpg"),
	},
	fb_h251_w06: {
		map: getLocaleAsset("tosize_furniture/texture/h251_w06/h251_w06-1.jpg"),
		specular: getLocaleAsset("tosize_furniture/texture/zwart/test/gloss-0.5k.jpg"),
	},
	fb_h397_bst: {
		map: getLocaleAsset("tosize_furniture/texture/h397_bst/h397_bst.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/bst.jpg"),
	},
	fb_h906_w06: {
		map: getLocaleAsset("tosize_furniture/texture/h906_w06/h906_w06.jpg"),
		specular: getLocaleAsset("tosize_furniture/texture/zwart/test/gloss-0.5k.jpg"),
	},
	fb_h597_w07: {
		map: getLocaleAsset("tosize_furniture/texture/h597_w07/h597_w07.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/w07-2.jpg"),
	},
	fb_h265_v1a: {
		map: getLocaleAsset("tosize_furniture/texture/h265_v1a/h265_v1a.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/h265_v1a-1.jpg"),
	},
	fb_h267_v1a: {
		map: getLocaleAsset("tosize_furniture/texture/h267_v1a/h267_v1a.jpg"),
		specular: getLocaleAsset("tosize_furniture/specular/h267_v1a.jpg"),
	},
} as const;
